import {useCallback, useEffect, useRef, useState} from 'react';
import {EmbeddingHealthDto} from '../apiClient/Api';
import {useApplicationContext} from '../ApplicationContext';

interface Options {
    /** Auto-refresh interval in ms. Set to 0 to disable. Defaults to 30s. */
    refreshIntervalMs?: number;
    /** If true, perform an immediate fetch on mount. Defaults to true. */
    fetchOnMount?: boolean;
}

interface EmbeddingHealth {
    health: EmbeddingHealthDto | null;
    loading: boolean;
    /** Manual refresh that the UI can wire to a button. */
    refresh: () => Promise<void>;
}

/**
 * Polls /Search/Health and exposes the latest snapshot. The first fetch is
 * fired on mount; subsequent refreshes happen on the configured interval and
 * on demand. Errors are swallowed so the UI degrades gracefully — callers can
 * detect the offline state via `health === null` after a refresh attempt.
 */
export function useEmbeddingHealth({refreshIntervalMs = 30000, fetchOnMount = true}: Options = {}): EmbeddingHealth {
    const context = useApplicationContext();
    const [health, setHealth] = useState<EmbeddingHealthDto | null>(null);
    const [loading, setLoading] = useState(false);
    const apiRef = useRef(context.api.search);
    apiRef.current = context.api.search;

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiRef.current.searchHealth();
            setHealth(response.data ?? null);
        } catch {
            // Health endpoint failures are not actionable from the UI; keep
            // the last known snapshot rather than nuking it.
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (fetchOnMount) refresh();
        if (refreshIntervalMs <= 0) return;
        const id = window.setInterval(refresh, refreshIntervalMs);
        return () => window.clearInterval(id);
    }, [fetchOnMount, refreshIntervalMs, refresh]);

    return {health, loading, refresh};
}
